/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {merge} from 'rxjs/operators';
import * as Stomp from 'stompjs';
import * as Frame from 'stompjs';
import * as Client from 'stompjs';
import * as SockJS from 'sockjs-client';

import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, ReplaySubject, Observer} from 'rxjs';
import {environment} from '../../environments/environment';
import { LogService } from 'src/app/services';

const DEFAULT_CACHE_SIZE: number = 100;

class TopicSubscription {
  subscription: any = null;
  subject: ReplaySubject<any>;

  constructor(cacheSize: number = DEFAULT_CACHE_SIZE) {
    this.subject = new ReplaySubject(cacheSize);
  }

  hasSubscribers(): boolean {
    return this.subject.observers.length > 0;
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.subject.unsubscribe();
  }
}

@Injectable()
export class StompClient {
  private socket: any;
  private client: Client;
  private connectionSource: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private connecting: boolean = false;
  private isReconnecting: boolean = false;
  private reConnectTimer: any;
  private topicSubscriptions: Map<string, TopicSubscription> = new Map();

  constructor(private logService :LogService
  ) {
    this.connect({ACCESS_TOKEN: localStorage.getItem('R-CRM-TOKEN')});
  }

  subscribe(topic: string, params: Object = {}): Observable<any> {
    let key = this.toKey(topic, params);

    if (!this.topicSubscriptions.has(key)) {
      this.createSubscription(key, topic, params);
    }

    let observable: Observable<any> = this.topicSubscriptions.get(key).subject.asObservable();
    return observable.pipe(merge(this.tearDownObservable(key)));
  }

  unsubscribe(key: string): void {
    let topicSubscription: TopicSubscription = this.topicSubscriptions.get(key);

    if (topicSubscription && !topicSubscription.hasSubscribers()) {
      topicSubscription.unsubscribe();
      this.topicSubscriptions.delete(key);
    }
  }

  send(topic: string, headers: Object = {}, body: any = ''): void {
    this.connect().subscribe((connected: boolean) => {
      if (connected) {
        this.client.send(topic, headers, body);
      }
    });
  }

  connect(headers: any = {}): Observable<boolean> {
    if (!this.connecting && !this.isConnected()) {
      this.initConnection(headers);
      this.connecting = true;
    }

    return this.connectionSource.asObservable();
  }

  disconnect(): void {
    if (this.isConnected()) {
      this.client.disconnect();
      this.socket.close();
      this.connectionSource.next(false);
    }

    this.topicSubscriptions.forEach((subscription: TopicSubscription) => subscription.unsubscribe());
    this.topicSubscriptions.clear();

    this.isReconnecting = true;

    if(!this.reConnectTimer)
      this.reConnectTimer = setInterval(()=>{
        this.logService.log('Reconnecting to Socket Server');
        this.connect({ACCESS_TOKEN: localStorage.getItem('R-CRM-TOKEN')});
      }, 5000);
  }

  isConnected(): boolean {
    return this.socket && this.client && this.client.connected;
  }

  private toKey(topic: string, params: Object): string {
    return `${topic}${JSON.stringify(params)}`;
  }

  private initConnection(headers: any): void {
    this.socket = new SockJS(`${environment.API_URL}/socket`);
    this.client = Stomp.over(this.socket);
    if (environment.DISABLE_LOG)
      this.client.debug = null;

    this.client.connect(headers,
      (frame: Frame) => {
        if(this.connecting)
          clearInterval(this.reConnectTimer);

        this.connectionSource.next(true);
        this.connecting = false;

        if(this.isReconnecting){
          this.isReconnecting = false;
        }
      },
      (error: string) => {
        this.disconnect();
        this.connectionSource.next(false);
        this.connecting = false;
      });
  }

  private createSubscription(key: string, topic: string, params: any): void {
    this.topicSubscriptions.set(key, new TopicSubscription());

    this.connect().subscribe((connected: boolean) => {
      let topicSubscription: TopicSubscription = this.topicSubscriptions.get(key);

      if (connected && this.canSubscribe(topicSubscription)) {
        topicSubscription.subscription = this.client.subscribe(topic,
          (message: Frame) => this.emitMessage(key, message), params);
      }
    });
  }

  private canSubscribe(topicSubscription: TopicSubscription): boolean {
    return topicSubscription && !topicSubscription.subscription;
  }

  private emitMessage(key: string, message: Frame): void {
    let topicSubscription = this.topicSubscriptions.get(key);
    if (topicSubscription.hasSubscribers()) {
      topicSubscription.subject.next(JSON.parse(message.body));
    }
  }

  private tearDownObservable(key: string): Observable<any> {
    return Observable.create((observer: Observer<any>) => {
      return (): any => this.unsubscribe(key);
    });
  }
}
